import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useCallback } from "react";

export const readAllCoursesForDashboardAsync = createAsyncThunk(
  "course/readAllCoursesForDashboard",
  async ({ searchTerm, currentPage, showLoading }, { dispatch }) => {
    if (showLoading) dispatch(setLoading(true));
    try {
      const response = await axios.get("/api/admin/courses", {
        params: { searchTerm, page: currentPage }
      });
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      if (showLoading) dispatch(setLoading(false));
    }
  }
);

export const useCourseOptions = () => {
  const dispatch = useDispatch();
  const { courseOptions } = useSelector(state => state.course);
  const { currentUser } = useSelector(state => state.auth);

  useEffect(() => {
    if (currentUser.role !== "creator" && currentUser.role !== "admin") return;
    if (!courseOptions.length) {
      dispatch(getCoursesAsync());
    }
  }, [dispatch, currentUser, courseOptions.length]);

  return courseOptions.map(course => ({ id: course.id, label: course.title }));
};

export const getCoursesAsync = createAsyncThunk("course/getCourses", async (_, { getState }) => {
  try {
    const response = await axios.get("/api/admin/courses");
    return { courses: response.data };
  } catch (error) {
    console.error("Failed to get courses.", error);
    throw error;
  }
});

const courseSlice = createSlice({
  name: "course",
  initialState: {
    courses: [],
    searchTerm: "",
    loading: false,
    error: null,
    initialized: false,
    courseOptions: []
  },
  reducers: {
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    clearError: (state, action) => {
      if (state.error) state.error[action.payload.field] = null;
    },
    clearErrors: state => {
      state.error = null;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(readAllCoursesForDashboardAsync.fulfilled, (state, action) => {
        state.courses = action.payload;
        state.initialized = true;
      })
      .addCase(readAllCoursesForDashboardAsync.rejected, (state, action) => {
        state.error = action.error.message;
      })
      .addCase(getCoursesAsync.pending, state => {
        state.loading = true;
      })
      .addCase(getCoursesAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.courseOptions = action.payload.courses;
      })
      .addCase(getCoursesAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error;
      });
  }
});

export const { setSearchTerm, setLoading, clearError, clearErrors } = courseSlice.actions;

export default courseSlice.reducer;

export const useCourses = () => {
  const dispatch = useDispatch();
  const { courses, searchTerm, loading, initialized } = useSelector(state => state.course);

  const fetchCourses = useCallback(
    async ({ searchTerm = "", showLoading = true }) => {
      await dispatch(readAllCoursesForDashboardAsync({ searchTerm, currentPage: 1, showLoading }));
    },
    [dispatch]
  );

  useEffect(() => {
    if (!initialized) fetchCourses({ searchTerm });
  }, [fetchCourses, searchTerm, initialized]);

  return { courses, searchTerm, loading, fetchCourses };
};
